import React, { createContext, useContext, useState, useMemo } from 'react';
import { QuestionKey } from '@/components/UserDataForm';

export type Goal = 'general_wellbeing' | 'energy' | 'mental_clarity' | 'mood' | 'sleep' | 'immune_support' | 'fitness';

export type FrequencyOption = 'never' | 'once_a_week' | 'two_three_times_a_week' | 'four_plus_times_a_week';

export type StressLevel = 'very_low' | 'low' | 'moderate' | 'high';

export type ExerciseType = 'strength' | 'cardio_endurance' | 'sports_recreational' | 'mind_body_flexibility' | 'general_fitness';

export interface UserDataType {
  age: number | null;
  gender: 'male' | 'female' | null;
  goals: { value: Goal; priority: number }[];
  currentStep: string;
  isBasicFormOnly: boolean;
  currentSupplements: string[];
  healthConcerns: string[];
  exerciseFrequency: FrequencyOption | '';
  exerciseIntensity: string;
  exerciseType: ExerciseType[] | [];
  stressLevel: StressLevel | '';
  alcoholConsumption: FrequencyOption | '';
  smokingStatus: string;
  smoking: boolean | null;
  meatConsumption: FrequencyOption | '';
  seafoodConsumption: FrequencyOption | '';
  dairyConsumption: FrequencyOption | '';
  dietaryRequirements: string[];
  lastQuestion?: QuestionKey;
  dangerMode?: boolean;
}

export const defaultUserData: UserDataType = {
  age: null,
  gender: null,
  goals: [],
  currentStep: 'basic',
  isBasicFormOnly: false,
  currentSupplements: [],
  healthConcerns: [],
  exerciseFrequency: '',
  exerciseIntensity: '',
  exerciseType: [],
  stressLevel: '',
  alcoholConsumption: '',
  smokingStatus: '',
  smoking: null,
  meatConsumption: '',
  seafoodConsumption: '',
  dairyConsumption: '',
  dietaryRequirements: [],
  lastQuestion: undefined
};

type UserDataContextType = {
  userData: UserDataType;
  setUserData: React.Dispatch<React.SetStateAction<UserDataType>>;
  isDataComplete: boolean;
};

const UserDataContext = createContext<UserDataContextType | undefined>(undefined);

export const UserDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [userData, setUserData] = useState<UserDataType>(defaultUserData);
  
  // Determine if the basic user data is complete
  const isDataComplete = useMemo(() => {
    return (
      userData.age !== null &&
      userData.gender !== null &&
      userData.goals.length > 0
    );
  }, [userData.age, userData.gender, userData.goals]);

  return (
    <UserDataContext.Provider value={{ userData, setUserData, isDataComplete }}>
      {children}
    </UserDataContext.Provider>
  );
};

export const useUserData = (): UserDataContextType => {
  const context = useContext(UserDataContext);
  if (!context) {
    throw new Error('useUserData must be used within a UserDataProvider');
  }
  return context;
};
